import React from 'react';
import {
  Header,
  HeaderName,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenuButton,
  SideNav,
  SideNavItems,
  SideNavLink,
} from 'carbon-components-react';
import {
  Notification,
  Settings,
  Logout,
} from '@carbon/icons-react';
import { selectFilteredServices, selectIsAdmin, selectMe, services, setMe, setToken } from '../../AppSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { isDevelopmentMode, isProductionBuild } from '../../config/build';
import { getAvailableProductTools, getProduct, ToolId } from '../../config/products';
import './_app_header.scss';

interface AppHeaderProps {
  hideSideNav?: boolean;
  activeService?: keyof typeof services;
  titleOverride?: string;
  selectedProductId?: string | null;
  onGoHome?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  hideSideNav = false,
  activeService = 'home',
  titleOverride,
  selectedProductId,
  onGoHome,
}) => {

  const dispatch = useAppDispatch();
  const me = useAppSelector(selectMe);
  const isAdmin = useAppSelector(selectIsAdmin);
  const filteredServices = useAppSelector(selectFilteredServices);
  const navigate = useNavigate();
  const location = useLocation();

  const goHome = () => {
    window.sessionStorage.removeItem('media-suite-product');
    onGoHome?.();
    navigate('/');
  };
  const goToService = (serviceId: keyof typeof services) => {
    if (serviceId === 'home') {
      navigate('/');
    } else {
      navigate(`/service/${serviceId}`);
    }
  };

  const currentTitle = titleOverride ?? services[activeService]?.name ?? '';
  const selectedProduct = getProduct(selectedProductId);
  const productTools = getAvailableProductTools(
    selectedProduct,
    filteredServices as ToolId[],
  );

  return (
    <>
      <Header aria-label="Media Suite App" className="app-header">
        {!hideSideNav && <HeaderMenuButton aria-label="Open navigation" />}
        <HeaderName prefix="x.dream" onClick={goHome} className="app-header__brand">
          <span className="app-header__suite">Media Suite</span>
          <span className="app-header__divider" />
          <strong className="app-header__title">{currentTitle}</strong>
        </HeaderName>
        <HeaderGlobalBar>
          {!isProductionBuild && (
            <HeaderGlobalAction aria-label="Notifications">
              <Notification />
            </HeaderGlobalAction>
          )}
          {isAdmin && (
            <HeaderGlobalAction
              aria-label="Open settings"
              tooltipAlignment="center"
              onClick={() => {
                if (!location.pathname.startsWith('/settings')) {
                  navigate('/settings');
                }
              }}
            >
              <Settings />
            </HeaderGlobalAction>
          )}
          <HeaderGlobalAction
            aria-label="Open account settings"
            onClick={() => navigate('/settings/account')}
            style={{ whiteSpace: 'nowrap', minWidth: 100 }}
          >
            <span>{me?.name}</span>
          </HeaderGlobalAction>
          {!isDevelopmentMode && (
            <HeaderGlobalAction aria-label="Logout" onClick={() => { dispatch(setMe(null)); dispatch(setToken(null)); }}>
              <Logout />
            </HeaderGlobalAction>
          )}
        </HeaderGlobalBar>
      </Header>

      {!hideSideNav && (
        <SideNav aria-label="Product tools" isFixedNav expanded className="workspace-nav">
          <SideNavItems>
            {productTools.map(key => (
              <SideNavLink
                key={key}
                renderIcon={services[key].icon}
                title={services[key].name}
                isActive={activeService === key}
                onClick={() => goToService(key)}
              />
            ))}
          </SideNavItems>
        </SideNav>
      )}
    </>
  );
};

export default AppHeader;
